/* ==========================================================================
   useAdminAuth Hook
   Task F20: Convenience hook for admin authentication
   ========================================================================== */

import { PharmacyUserRole } from '@/api/types'
import { useAdminAuthContext } from '@/context/AdminAuthContext'

/**
 * Hook to access admin auth state and methods.
 * Provides the same interface as useAdminAuthContext with better semantics.
 *
 * @example
 * ```tsx
 * function AdminComponent() {
 *   const { user, pharmacy, isAuthenticated, login, logout } = useAdminAuth()
 *
 *   if (!isAuthenticated) {
 *     return <AdminLoginPrompt />
 *   }
 *
 *   return <div>Welcome, {user?.name} from {pharmacy?.name}</div>
 * }
 * ```
 */
export function useAdminAuth() {
  return useAdminAuthContext()
}

/**
 * Get the current admin user if authenticated.
 * Returns null if not authenticated.
 */
export function useAdminUser() {
  const { user } = useAdminAuthContext()
  return user
}

/**
 * Get the current pharmacy for the admin.
 * Returns null if not authenticated.
 */
export function useAdminPharmacy() {
  const { pharmacy } = useAdminAuthContext()
  return pharmacy
}

/**
 * Check if the admin is currently authenticated.
 */
export function useIsAdminAuthenticated(): boolean {
  const { isAuthenticated } = useAdminAuthContext()
  return isAuthenticated
}

/**
 * Get the pharmacy slug for the authenticated admin.
 * Returns null if not authenticated or pharmacy not loaded.
 */
export function useAdminPharmacySlug(): string | null {
  const { pharmacy } = useAdminAuthContext()
  return pharmacy?.portal_slug ?? null
}

/**
 * Role-derived permission helpers for the pharmacy admin UI (PORTAL-94).
 *
 * These mirror the backend capability map for UX only — the API is the
 * authoritative guard, reading the live role from the database on every request.
 */
export function useAdminPermissions() {
  const { user } = useAdminAuthContext()
  const role = user?.role
  const isAdmin = role === PharmacyUserRole.ADMIN
  const isStaff = role === PharmacyUserRole.STAFF
  return {
    role,
    isAdmin,
    isStaff,
    /** Manager or Admin: may manage formulary, settings, billing, prescriptions
     *  and the team. Named explicitly rather than derived as ``!isStaff`` so an
     *  unresolved role (before `user` loads, or after logout) grants nothing. */
    canManage: isAdmin || role === PharmacyUserRole.MANAGER,
  }
}

export default useAdminAuth
